/**
 * The map shell every 2D twin map starts from: a Leaflet map with one of the
 * TurboTwin base layers, the zoom control, a fit to bounds and a size that
 * follows its container (a docked side panel resizes it with no window resize
 * event, and Leaflet would keep painting at the old size).
 *
 * Layers (markers, shapes, lines) are react-leaflet children. The host imports
 * `leaflet/dist/leaflet.css` once and sizes the element that holds the map;
 * the map fills it.
 */
import { forwardRef, useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { LayersControl, MapContainer, TileLayer, ZoomControl, useMap } from 'react-leaflet';
import {
  LatLngBounds,
  latLngBounds,
  type ControlPosition,
  type FitBoundsOptions,
  type LatLngBoundsExpression,
  type LatLngExpression,
  type Map as LeafletMap,
} from 'leaflet';
import { useElementResize } from '../../useElementResize';
import { TILE_LAYERS, tileLayerFor, type TileLayerId } from '../tileLayers';

/** `center`, `zoom`, `maxZoom`, `scrollWheelZoom`, `className` and `style` apply when the
 *  map mounts, as with react-leaflet's `MapContainer`; give it a new `key` to change them.
 *  The other props follow every render. */
export interface TurboTwinMapProps {
  /** The base layer shown. Default `'satellite'`. With `layerControl`, the one shown first. */
  layer?: TileLayerId;
  /** Show Leaflet's own base-layer switcher (Street, Terrain, Satellite) in this corner. */
  layerControl?: ControlPosition;
  /** The view before any fit. Default `[20, 0]` at zoom 2. */
  center?: LatLngExpression;
  zoom?: number;
  /** Highest zoom; past a layer's own deepest tiles Leaflet enlarges them. Default 19. */
  maxZoom?: number;
  /** Fit the view to these bounds, once per distinct `fitKey`. */
  fitBounds?: LatLngBoundsExpression | null;
  /** When to fit again, compared by identity. Null or undefined: whenever the bounds' box changes. */
  fitKey?: unknown;
  fitOptions?: FitBoundsOptions;
  /** Corner of the +/- zoom control; `false` hides it. Default `'topleft'`. */
  zoomControl?: ControlPosition | false;
  /** Default true. */
  scrollWheelZoom?: boolean;
  className?: string;
  /** Merged over the map element's `height: 100%; width: 100%`. */
  style?: CSSProperties;
  children?: ReactNode;
}

const NOT_FITTED = Symbol('not fitted');

function FitToBounds({
  bounds,
  fitKey,
  options,
}: {
  bounds: LatLngBoundsExpression | null;
  fitKey: unknown;
  options?: FitBoundsOptions;
}) {
  const map = useMap();
  const fittedKey = useRef<unknown>(NOT_FITTED);
  useEffect(() => {
    if (!bounds) return;
    const box = bounds instanceof LatLngBounds ? bounds : latLngBounds(bounds);
    const key = fitKey ?? box.toBBoxString();
    if (Object.is(key, fittedKey.current)) return;
    map.fitBounds(bounds, options);
    fittedKey.current = key;
  }, [map, bounds, fitKey, options]);
  return null;
}

function InvalidateSizeOnResize() {
  const map = useMap();
  const container = useRef<HTMLElement | null>(map.getContainer());
  useElementResize(container, () => map.invalidateSize());
  return null;
}

/** The map shell; the ref is the Leaflet map. */
export const TurboTwinMap = forwardRef<LeafletMap, TurboTwinMapProps>(function TurboTwinMap(
  {
    layer = 'satellite',
    layerControl,
    center = [20, 0],
    zoom = 2,
    maxZoom = 19,
    fitBounds = null,
    fitKey,
    fitOptions,
    zoomControl = 'topleft',
    scrollWheelZoom = true,
    className,
    style,
    children,
  },
  ref,
) {
  const base = tileLayerFor(layer);
  return (
    <MapContainer
      ref={ref}
      center={center}
      zoom={zoom}
      maxZoom={maxZoom}
      scrollWheelZoom={scrollWheelZoom}
      zoomControl={false}
      className={className}
      style={{ height: '100%', width: '100%', ...style }}
    >
      {zoomControl && <ZoomControl position={zoomControl} />}
      {layerControl ? (
        <LayersControl position={layerControl}>
          {TILE_LAYERS.map((l) => (
            <LayersControl.BaseLayer key={l.id} name={l.label} checked={l.id === base.id}>
              <TileLayer
                url={l.url}
                attribution={l.attribution}
                maxNativeZoom={l.maxNativeZoom}
                maxZoom={maxZoom}
              />
            </LayersControl.BaseLayer>
          ))}
        </LayersControl>
      ) : (
        <TileLayer
          key={base.id}
          url={base.url}
          attribution={base.attribution}
          maxNativeZoom={base.maxNativeZoom}
          maxZoom={maxZoom}
        />
      )}
      <InvalidateSizeOnResize />
      <FitToBounds bounds={fitBounds} fitKey={fitKey} options={fitOptions} />
      {children}
    </MapContainer>
  );
});
